import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, ShieldCheck, Users, Trophy, Medal, Broadcast, CalendarCheck, Buildings, Clock, Funnel, ArrowClockwise } from "@phosphor-icons/react";

const CONTEXT_META = {
  tournament:  { label: "Torneo",      icon: Trophy,        hint: "Check-in a torneos" },
  league:      { label: "Liga",        icon: Medal,         hint: "Check-in a ligas" },
  marcador:    { label: "Marcador",    icon: Broadcast,     hint: "Check-in a marcadores / retas" },
  event:       { label: "Evento",      icon: CalendarCheck, hint: "Check-in a eventos" },
  club_access: { label: "Acceso Club", icon: Buildings,     hint: "Puerta / control de acceso físico" },
};

const ROLE_LABEL = { jugador: "Jugador", directivo: "Directivo", profesor: "Profesor" };

export default function AdminAccessControl() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState({ contexts: [], roles: [], matrix: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("matrix"); // matrix | logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filterContext, setFilterContext] = useState("");
  const [filterDecision, setFilterDecision] = useState("");

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/dashboard"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/access/permissions");
      setMeta(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterContext) params.set("context", filterContext);
      if (filterDecision) params.set("decision", filterDecision);
      params.set("limit", "100");
      const { data } = await api.get(`/access/logs?${params.toString()}`);
      setLogs(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLogsLoading(false); }
  };

  useEffect(() => { if (tab === "logs") loadLogs(); /* eslint-disable-next-line */ }, [tab, filterContext, filterDecision]);

  const toggle = (ctx, role) => {
    setMeta((m) => ({
      ...m,
      matrix: {
        ...m.matrix,
        [ctx]: { ...m.matrix[ctx], [role]: !m.matrix[ctx]?.[role] },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/access/permissions", { matrix: meta.matrix });
      toast.success("Permisos guardados");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    const by = { total: logs.length, authorized: 0, denied: 0 };
    for (const l of logs) { if (l.decision === "authorized") by.authorized++; else if (l.decision === "denied") by.denied++; }
    return by;
  }, [logs]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-access-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1.5">
            <ShieldCheck size={12} weight="fill" /> Admin · Control de Acceso
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tighter">Permisos de escaneo QR</h1>
          <p className="text-xs text-gray-500 mt-1">Autoriza qué roles pueden escanear códigos QR para check-in y control de acceso a clubes.</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)} className="border-white/20 text-gray-300 hover:text-[#CCFF00] text-xs uppercase h-9" data-testid="access-back">
          <ArrowLeft size={14} className="mr-1" /> Volver
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10" data-testid="access-tabs">
        {[
          { id: "matrix", label: "Matriz de permisos", icon: ShieldCheck },
          { id: "logs",   label: "Historial de escaneos", icon: Clock },
        ].map((t) => {
          const Icon = t.icon;
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors ${on ? "text-[#CCFF00] border-[#CCFF00]" : "text-gray-400 border-transparent hover:text-white"}`}
              data-testid={`access-tab-${t.id}`}
            >
              <Icon size={14} weight={on ? "fill" : "regular"} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "matrix" && (
        <div className="card-tactical p-4" data-testid="access-matrix">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <p className="text-xs text-gray-400">
              <b className="text-white">Admin</b> siempre puede escanear todo. Directivo y Profesor por default pueden escanear todos los contextos. Jugador por default NO puede escanear.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 pr-4">Contexto</th>
                  {meta.roles.map((r) => (
                    <th key={r} className="py-2 px-3 text-center">{ROLE_LABEL[r] || r}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meta.contexts.map((ctx) => {
                  const CtxIcon = CONTEXT_META[ctx]?.icon || ShieldCheck;
                  return (
                    <tr key={ctx} className="border-b border-white/5" data-testid={`access-row-${ctx}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <CtxIcon size={16} className="text-[#CCFF00]" />
                          <div>
                            <p className="text-white font-semibold text-sm">{CONTEXT_META[ctx]?.label || ctx}</p>
                            <p className="text-[10px] text-gray-500">{CONTEXT_META[ctx]?.hint}</p>
                          </div>
                        </div>
                      </td>
                      {meta.roles.map((r) => {
                        const on = !!meta.matrix?.[ctx]?.[r];
                        return (
                          <td key={r} className="py-3 px-3 text-center">
                            <button
                              onClick={() => toggle(ctx, r)}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 border rounded text-[10px] uppercase tracking-widest font-bold transition-colors ${on ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-black/40 text-gray-500 border-white/10 hover:border-white/25"}`}
                              data-testid={`access-toggle-${ctx}-${r}`}
                            >
                              {on ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="regular" />}
                              {on ? "Permitido" : "Denegado"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={load} className="text-gray-400 text-xs uppercase h-9" data-testid="access-reload">
              <ArrowClockwise size={14} className="mr-1" /> Recargar
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide" data-testid="access-save">
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="card-tactical p-3 flex flex-wrap items-center gap-3" data-testid="access-logs-filters">
            <Funnel size={14} className="text-[#CCFF00]" />
            <select
              value={filterContext}
              onChange={(e) => setFilterContext(e.target.value)}
              className="appearance-none bg-black/60 border border-white/15 text-[11px] uppercase tracking-widest px-3 py-1.5 text-gray-300 focus:border-[#CCFF00] focus:outline-none"
              data-testid="logs-filter-context"
            >
              <option value="">Todos los contextos</option>
              {meta.contexts.map((c) => (
                <option key={c} value={c}>{CONTEXT_META[c]?.label || c}</option>
              ))}
            </select>
            <select
              value={filterDecision}
              onChange={(e) => setFilterDecision(e.target.value)}
              className="appearance-none bg-black/60 border border-white/15 text-[11px] uppercase tracking-widest px-3 py-1.5 text-gray-300 focus:border-[#CCFF00] focus:outline-none"
              data-testid="logs-filter-decision"
            >
              <option value="">Autorizados y denegados</option>
              <option value="authorized">Solo autorizados</option>
              <option value="denied">Solo denegados</option>
            </select>
            <Button variant="ghost" onClick={loadLogs} className="ml-auto text-xs uppercase text-gray-400 h-8" data-testid="logs-reload">
              <ArrowClockwise size={12} className="mr-1" /> Actualizar
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3" data-testid="access-logs-stats">
            <div className="card-tactical p-3 text-center">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Total</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-white">{counts.total}</p>
            </div>
            <div className="card-tactical p-3 text-center border-[#CCFF00]/30">
              <p className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Autorizados</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">{counts.authorized}</p>
            </div>
            <div className="card-tactical p-3 text-center border-red-500/30">
              <p className="text-[10px] uppercase tracking-widest text-red-400">Denegados</p>
              <p className="font-['Barlow_Condensed'] text-2xl font-black text-red-400">{counts.denied}</p>
            </div>
          </div>

          {/* List */}
          <div className="card-tactical p-3" data-testid="access-logs-list">
            {logsLoading ? (
              <div className="py-6 text-center text-xs text-gray-500">Cargando…</div>
            ) : logs.length === 0 ? (
              <p className="text-xs text-gray-500 italic text-center py-6">Sin escaneos registrados aún.</p>
            ) : (
              <div className="space-y-2">
                {logs.map((l) => {
                  const CtxIcon = CONTEXT_META[l.context]?.icon || ShieldCheck;
                  const isAuth = l.decision === "authorized";
                  return (
                    <div
                      key={l.id}
                      className={`p-3 border-l-4 bg-black/40 border border-white/5 rounded flex items-center gap-3 ${isAuth ? "border-l-[#CCFF00]" : "border-l-red-500"}`}
                      data-testid={`log-${l.id}`}
                    >
                      {isAuth ? <CheckCircle size={22} weight="fill" className="text-[#CCFF00] shrink-0" /> : <XCircle size={22} weight="fill" className="text-red-500 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-white font-semibold truncate">{l.target_name || "—"}</p>
                          <Badge variant="outline" className="text-[9px] uppercase h-4 border-white/10 text-gray-400">{l.target_role}</Badge>
                          {l.target_category && <Badge variant="outline" className="text-[9px] uppercase h-4 border-white/10 text-gray-400">Cat {l.target_category}</Badge>}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                          <CtxIcon size={11} className="text-[#CCFF00]" />
                          <span className="uppercase tracking-widest">{CONTEXT_META[l.context]?.label || l.context}</span>
                          {l.gate && <span>· 🚪 {l.gate}</span>}
                          {l.reason && <span className="text-red-400">· {l.reason}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-gray-500">{new Date(l.scanned_at).toLocaleString()}</p>
                        <p className="text-[10px] text-gray-600">por {l.scanner_name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
